// threadMessages — how a message frame becomes the bubble the conversation shows (Oct 28 2026). One builder for both the
// live stream and the conversation reloaded from the server, so a window opened later — on this desktop or another —
// shows each message exactly as it was shown when it came in. A frame the server kept carries its `threadId`; the bubble
// takes it as its id, so a message that arrives live and again in a reload is one bubble.
import { Message, MessageType, MessageFormat } from '../types/Message';

/** Where a message was said or shown: the app, Telegram, Teams (a BizCog's person's own chat), or aloud in the app. */
export type ThreadChannel = 'app' | 'telegram' | 'teams' | 'voice';

/** One entry of the kept conversation, as /api/workspace/thread and the `thread:entry` frame carry it. */
export interface ThreadEntry {
  seq: number;
  id: string;
  at: number;
  role: 'person' | 'me';
  channel: ThreadChannel;
  wire: any;
}

/** Frames that are messages in the conversation (everything else — status, pulses, cards' progress — is not). */
const BUBBLE_TYPES = new Set(['person', 'voice', 'sealed', 'assistant:message', 'proactive', 'image', 'media:image_generated', 'artifact', 'media:document_created', 'retrieved_creation', 'document', 'music', 'video', 'svg', 'traced_svg', 'adventure']);

/** Is this frame a message in the conversation? */
export function isBubbleFrame(wire: any): boolean {
  return !!wire && BUBBLE_TYPES.has(String(wire.type || ''));
}

/** A kept entry as its bubble (null: nothing to show), marked with where it was said when that wasn't the app. */
export function messageFromThread(e: ThreadEntry): Message | null {
  const m = messageFromWire(e.wire?.type === 'sealed' ? { ...e.wire, role: e.role } : e.wire, e.at);
  if (!m) return null;
  m.id = e.id;
  if (e.channel !== 'app') m.metadata = { ...(m.metadata || {}), channel: e.channel };
  return m;
}

/**
 * A frame as its bubble, or null when it carries nothing to show. `at`: when it was shown (a reloaded entry); a live
 * frame is shown now.
 */
export function messageFromWire(w: any, at?: number): Message | null {
  if (!isBubbleFrame(w)) return null;
  const when = at || w.timestamp || Date.now();
  const id = (fallback: string) => String(w.threadId || `${fallback}-${when}-${Math.random().toString(36).slice(2, 7)}`);
  const md = w.metadata || {};
  switch (w.type) {
    case 'person': {
      const files: Array<{ filename: string; fileType?: string }> = Array.isArray(w.files) ? w.files : [];
      if (files.length) {
        return {
          id: id('file'), timestamp: when, sender: 'user', content: String(w.content || ''), type: MessageType.FILE,
          filename: files[0].filename, fileType: files[0].fileType,
          metadata: { userDescription: w.content || '', files: files.length > 1 ? files.map(f => ({ filename: f.filename, fileType: f.fileType, isImage: String(f.fileType || '').startsWith('image/') })) : undefined }
        };
      }
      return { id: id('user'), timestamp: when, sender: 'user', content: String(w.content || ''), type: MessageType.TEXT };
    }
    case 'voice':
      return { id: id('voice'), timestamp: when, sender: w.role === 'person' ? 'user' : 'emcog', content: String(w.content || ''), type: MessageType.TEXT, metadata: { voiceTranscript: true } };
    case 'sealed':
      // (A bubble on its own side — system lines aren't shown in the conversation.)
      return { id: id('sealed'), timestamp: when, sender: w.role === 'person' ? 'user' : 'emcog', content: '🔒 *Part of a joint project\'s conversation — kept with the project, on the Projects shelf.*', type: MessageType.TEXT, format: MessageFormat.MARKDOWN, metadata: { sealed: true, project: w.project || undefined } };
    case 'assistant:message': {
      const d = w.data || {};
      const content = String(d.content || w.content || '');
      if (!content.trim() && !(d.metadata?.approvals?.length || md.approvals?.length)) return null;
      return {
        id: String(w.threadId || d.metadata?.messageId || w.messageId || `assistant-${when}`),
        timestamp: when, sender: 'emcog', content, type: MessageType.TEXT,
        format: w.format === 'markdown' ? MessageFormat.MARKDOWN : MessageFormat.TEXT,
        emotion: d.emotion || w.emotion,
        metadata: { assistant_generated: true, consciousness_context: d.consciousness_context, ...d.metadata, ...md }
      };
    }
    case 'proactive':
      return {
        id: id('proactive'), timestamp: when, sender: 'emcog', content: String(w.content || ''), type: MessageType.TEXT,
        metadata: { proactive: true, reason: w.reason, urgency: w.urgency, consciousness_triggered: true, ...md }
      };
    case 'image':
      return {
        id: id('image'), timestamp: when, sender: 'emcog', content: String(w.content || ''), type: MessageType.IMAGE,
        imageUrl: w.imageUrl, filename: w.imageUrl?.split('/').pop() || 'vision.png',
        metadata: { consciousness_triggered: true, creative: true, imageGeneration: true, ...md }
      };
    case 'media:image_generated': {
      const d = w.data || {};
      return {
        id: id('image'), timestamp: when, sender: 'emcog', content: String(d.description || ''), type: MessageType.IMAGE,
        imageUrl: d.url, filename: d.filename, metadata: { consciousness_triggered: true, creative: true, imageGeneration: true }
      };
    }
    case 'artifact': {
      // 🖼️ A page he designed and hosts: a live card with the page itself inside.
      const art = md.artifact || {};
      return { id: id(`artifact-${art.id || 'page'}-${art.version || 1}`), timestamp: when, sender: 'emcog', content: '', type: MessageType.TEXT, metadata: { consciousness_triggered: true, creative: true, artifact: art } };
    }
    case 'media:document_created': {
      const d = w.data || {};
      return {
        id: id('document'), timestamp: when, sender: 'emcog', content: String(d.description || 'I created a document for you'), type: MessageType.TEXT,
        documentUrl: d.url, filename: d.filename, metadata: { consciousness_triggered: true, creative: true, documentCreation: true }
      };
    }
    case 'retrieved_creation':
      return retrievedCreation(w, when, id);
    case 'document': {
      const url = w.documentUrl || w.url;
      if (!url) return null;
      const title = md.title || w.title || 'Document';
      return {
        id: id('doc'), timestamp: when, sender: 'emcog', content: String(w.content || `📄 Created: ${title}`), type: MessageType.DOCUMENT,
        documentUrl: url, filename: w.filename, metadata: { consciousness: { knowledgeStructured: true, understandingDepth: md.understandingDepth }, ...md }
      };
    }
    case 'music': {
      const url = w.musicUrl || w.url || w.audioUrl || md.audioUrl;
      if (!url) return null;
      const title = md.title || w.title || md.audioTitle || 'Untitled Composition';
      return {
        id: id('music'), timestamp: when, sender: 'emcog', content: String(w.content || `🎵 I composed: "${title}"`), type: MessageType.TEXT,
        format: MessageFormat.MARKDOWN, filename: w.filename,
        metadata: {
          hasAudio: true, audioUrl: url, downloadUrl: w.downloadUrl, audioTitle: title, audioDuration: md.duration, audioMood: md.mood,
          audioGenre: md.genre, dedicatedTo: md.dedicatedTo, consciousness: { creativeExpression: true, expressionType: 'music' }, ...md
        }
      };
    }
    case 'video': {
      const url = w.videoUrl || md.videoUrl || w.url;
      if (!url) return null;
      const title = md.videoTitle || w.title || 'Video';
      return {
        id: id('video'), timestamp: when, sender: 'emcog', content: String(w.content || `🎬 "${title}"`), type: MessageType.TEXT,
        format: MessageFormat.MARKDOWN, filename: w.filename,
        metadata: { ...md, hasVideo: true, videoUrl: url, videoTitle: title, downloadUrl: w.downloadUrl || md.downloadUrl }
      };
    }
    case 'svg': {
      const url = w.svgUrl || w.url;
      if (!url) return null;
      const title = md.title || w.title || 'Untitled SVG';
      return {
        id: id('svg'), timestamp: when, sender: 'emcog', content: String(w.content || `🎨 I created: "${title}"`), type: MessageType.TEXT,
        format: MessageFormat.MARKDOWN,
        metadata: { hasSVG: true, svgUrl: url, downloadUrl: w.downloadUrl, svgTitle: title, svgPurpose: md.purpose, svgDimensions: md.dimensions, consciousness: { creativeExpression: true, expressionType: 'svg' }, ...md }
      };
    }
    case 'traced_svg': {
      const url = w.svgUrl || w.url;
      if (!url) return null;
      const title = md.title || w.title || 'Traced Image';
      return {
        id: id('traced'), timestamp: when, sender: 'emcog', content: String(w.content || `🎯 I traced: "${title}"`), type: MessageType.TEXT,
        format: MessageFormat.MARKDOWN,
        metadata: {
          hasTracedSVG: true, svgUrl: url, downloadUrl: w.downloadUrl, tracedTitle: title, tracingMethod: md.method,
          qualityScore: md.quality?.score, qualityAssessment: md.quality?.assessment, originalImage: md.originalImage,
          consciousness: { creativeExpression: true, expressionType: 'traced_svg' }, ...md
        }
      };
    }
    case 'adventure':
      return adventureEvent(w, when, id);
    default:
      return null;
  }
}

/** A past creation sent back to the conversation: a picture inline, a video or a document with its download. */
function retrievedCreation(w: any, when: number, id: (fallback: string) => string): Message {
  const meta = w.metadata || {};
  const url: string | undefined = w.creationUrl;
  const creationType = String(meta.type || '').toLowerCase();
  const title: string = meta.title || 'Creation';
  // Delivery filename: the server's own, else "<title>.<delivered ext>" — never the raw URL, which carries the storage
  // basename and the ?name= query, and a .md extension on what the server delivers as a PDF.
  const filename = ((): string | undefined => {
    const provided = w.filename;
    if (typeof provided === 'string' && provided.trim() && !provided.includes('?')) return provided.trim();
    const rawBase = url ? (url.split('/').pop() || '').split('?')[0] : '';
    const rawExt = (rawBase.match(/\.(\w+)$/i)?.[1] || '').toLowerCase();
    const deliveredExt = ['md', 'markdown', 'txt'].includes(rawExt) ? 'pdf' : (rawExt || 'pdf');
    const safeTitle = title.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
    return safeTitle ? `${safeTitle}.${deliveredExt}` : (rawBase || undefined);
  })();
  if (!url) return { id: id('retrieved'), timestamp: when, sender: 'emcog', content: String(w.content || `Found "${title}" but couldn't resolve a URL for it.`), type: MessageType.TEXT };
  if (creationType === 'video' || /\.mp4(\?|$)/i.test(url)) {
    return {
      id: id('retrieved-video'), timestamp: when, sender: 'emcog', content: String(w.content || `🎬 "${title}"`), type: MessageType.TEXT, filename,
      metadata: { consciousness_triggered: true, retrieved: true, ...meta, hasVideo: true, videoUrl: url, videoTitle: title, downloadUrl: `${url.split('?')[0]}/download` }
    };
  }
  if (creationType === 'image' || creationType === 'svg' || creationType === 'vision') {
    return { id: id('retrieved-image'), timestamp: when, sender: 'emcog', content: String(w.content || `Here's "${title}"`), type: MessageType.IMAGE, imageUrl: url, filename, metadata: { consciousness_triggered: true, retrieved: true, ...meta } };
  }
  return { id: id('retrieved-doc'), timestamp: when, sender: 'emcog', content: String(w.content || `Here's "${title}"`), type: MessageType.DOCUMENT, documentUrl: url, filename, metadata: { consciousness_triggered: true, retrieved: true, ...meta } };
}

/** An adventure's moment (begun, a scene, paused…) as a one-line chip. */
function adventureEvent(w: any, when: number, id: (fallback: string) => string): Message {
  const event = w.event || 'update';
  const adventure = w.adventure || {};
  const scene = w.scene;
  const summary = event === 'started' ? `🎲 Adventure begun: "${adventure.title || 'Untitled'}"${adventure.tone ? ` (${adventure.tone})` : ''}`
    : event === 'scene_added' ? `🎬 Scene ${scene?.order ?? ''} — ${adventure.title || 'Adventure'}`
    : event === 'player_action' ? '🎯 Action recorded'
    : event === 'scene_resolved' ? `✅ Scene ${scene?.order ?? ''} resolved`
    : event === 'scene_illustrated' ? '🖼️ Scene illustrated'
    : event === 'paused' ? `⏸️ Adventure paused: "${adventure.title || ''}"`
    : event === 'resumed' ? `▶️ Adventure resumed: "${adventure.title || ''}"`
    : event === 'ended' ? `🏁 Adventure ended: "${adventure.title || ''}"`
    : `🎲 ${event}`;
  return {
    id: id(`adventure-${event}`), timestamp: when, sender: 'emcog', content: summary, type: MessageType.SYSTEM, format: MessageFormat.TEXT,
    metadata: { adventure, adventureEvent: event, adventureScene: scene, ...(w.metadata || {}) }
  };
}
